import { programmeRevisionsQuery } from "@common/api/maintenance-programme"
import type {
  ProgrammeDetailPublic,
  ProgrammeRevisionDetailPublic,
} from "@common/client"
import { useSuspenseQuery } from "@tanstack/react-query"
import { getRouteApi } from "@tanstack/react-router"
import {
  archivedText,
  isArchived,
} from "@/routes/_layout/maintenance-programmes/-lib/programmes"
import { revisionLabel } from "@/routes/_layout/maintenance-programmes/-lib/revision-label"
import { revisionStatusLabel } from "./revisions"
import { REVISIONS } from "./search"

const route = getRouteApi("/_layout/maintenance-programmes_/$programmeId")

export function editBlocker(
  programme: ProgrammeDetailPublic,
  revision: ProgrammeRevisionDetailPublic,
  canUpdate: boolean,
): string | undefined {
  if (!canUpdate) return "You can view tasks but not change them."
  if (isArchived(programme)) return archivedText(programme)
  return revision.status === "draft"
    ? undefined
    : `${revisionLabel(revision.number)} is ${revisionStatusLabel(revision.status).toLowerCase()}. Start a new revision to change its tasks.`
}

export function copyableRevisions(
  revisions: ProgrammeRevisionDetailPublic[],
): ProgrammeRevisionDetailPublic[] {
  return revisions.filter(({ status }) => status !== "draft")
}

export function isReleasable(revision: ProgrammeRevisionDetailPublic): boolean {
  return revision.status === "draft" || revision.status === "submitted"
}

export function isCurrent(
  programme: ProgrammeDetailPublic,
  revision: ProgrammeRevisionDetailPublic,
): boolean {
  return programme.current_revision?.id === revision.id && revision.in_effect
}

export function viewedRevision(
  programme: ProgrammeDetailPublic,
  revisions: ProgrammeRevisionDetailPublic[],
  wanted: string | undefined,
): ProgrammeRevisionDetailPublic | undefined {
  const byId = (revisionId: string | undefined) =>
    revisions.find(({ id }) => id === revisionId)
  return byId(wanted) ?? byId(programme.current_revision?.id) ?? revisions[0]
}

export function useViewedRevision(
  programme: ProgrammeDetailPublic,
): ProgrammeRevisionDetailPublic | undefined {
  const { revision } = route.useSearch()
  const { data } = useSuspenseQuery(
    programmeRevisionsQuery({ programme_id: programme.id, ...REVISIONS }),
  )
  return viewedRevision(programme, data.data, revision)
}
